/**
 * Up to four saved tanks — the bays along the back of the garage.
 *
 * A "favourite" is a hull-and-turret pair the player wants back in one click. It
 * is deliberately **not** a new thing the server knows about: the account
 * already has exactly one equipped hull and one equipped turret, and that
 * remains the only truth about what a player deploys with. A bay is a bookmark
 * pointing at a pair of item ids, so choosing one is an ordinary `equip` call
 * and nothing about the arena, the spec provider or the wire changes.
 *
 * That is why this lives in `localStorage` beside the map and mode preferences
 * rather than in Postgres. It is per browser, per player, and losing it costs a
 * player four clicks — not an item. Every hull and turret a bay names is still
 * owned and still equippable from the shop tabs.
 *
 * **Which bay is selected is derived, never stored.** The selected bay is
 * whichever one matches the tank the player currently has equipped, and if none
 * does, they are driving something unsaved and can park it. Storing the
 * selection separately was the first design and it was wrong twice over: it
 * could disagree with the equipped tank, and keeping the two in step meant
 * rewriting the selected bay whenever a hull was fitted elsewhere — which
 * silently destroyed a saved tank every time the player tried a different one.
 */
import { computed, ref } from 'vue';

/** One saved tank. Ids only — names, stats and art are all derived from these. */
export interface Favourite {
  hullId: string;
  turretId: string;
}

/**
 * The cap, and it is a design constraint rather than a storage limit.
 *
 * Four is enough to keep a brawler, a sniper and a scout beside a main, and few
 * enough that the row stays readable at a glance. A garage listing everything
 * owned would be the hulls tab, which already exists.
 */
export const MAX_FAVOURITES = 4;

/**
 * Storage is keyed per player.
 *
 * Two accounts on one browser is normal in development and real on a shared
 * machine, and one player's bays naming another's items would resolve to
 * nothing — a row of empty boxes that reads as data loss rather than as a
 * different account.
 */
const KEY_PREFIX = 'sow.favourites.';

const slots = ref<Favourite[]>([]);
let loadedFor: string | null = null;

export function useFavourites() {
  return {
    slots: computed(() => slots.value),
    /** Empty boxes to draw after the saved ones, so the cap is a visible shape. */
    freeSlots: computed(() => Math.max(0, MAX_FAVOURITES - slots.value.length)),
    canSave: computed(() => slots.value.length < MAX_FAVOURITES),

    load,
    indexOf,
    save,
    remove,
    makePrimary,
  };
}

/**
 * Reads this player's bays, seeding the first from whatever is equipped.
 *
 * The seed is written back, unlike everything else derived here: a player who
 * has never opened the lobby should find their tank already in bay 0 rather than
 * an empty garage, and that bay has to survive a reload or it reappears as
 * "unparked" the moment they fit something else.
 *
 * **An empty garage the player made is not the same as one they have never
 * had**, which is why `read` answers `null` rather than `[]` for a missing
 * entry. Now that a bay can be cleared, seeding on "nothing stored" alone would
 * put the current tank straight back into the bay somebody had just emptied, on
 * the next visit to the tab — a bookmark that reappears after being deleted.
 *
 * It is also safe to call before the garage has answered. Seeding needs to know
 * what is equipped, so a call with nothing equipped and nothing stored claims
 * nothing and leaves the next call to do the work; `GarageLobby` therefore
 * watches rather than calling once on mount.
 */
export function load(playerKey: string, equipped: Favourite | null): void {
  if (loadedFor === playerKey) return;

  const stored = read(playerKey);
  if (stored) {
    loadedFor = playerKey;
    slots.value = stored;
    return;
  }
  if (!equipped) {
    slots.value = [];
    return;
  }
  loadedFor = playerKey;
  slots.value = [equipped];
  persist();
}

/** Which bay holds this pair, or -1. The selection, derived. */
export function indexOf(fav: Favourite | null): number {
  if (!fav) return -1;
  return slots.value.findIndex((s) => s.hullId === fav.hullId && s.turretId === fav.turretId);
}

/** Saves a pair into the first free bay. Refuses a duplicate and the cap. */
export function save(fav: Favourite): 'saved' | 'duplicate' | 'full' {
  if (indexOf(fav) >= 0) return 'duplicate';
  if (slots.value.length >= MAX_FAVOURITES) return 'full';
  slots.value = [...slots.value, fav];
  persist();
  return 'saved';
}

/**
 * Clears a bay.
 *
 * Nothing here has to fix up a selection, because there is none to fix: the
 * selected bay is recomputed from the equipped tank. Clearing the bay you are
 * driving simply means you are now driving something unsaved, which is a real
 * state and is drawn as one.
 */
export function remove(index: number): void {
  if (!slots.value[index]) return;
  slots.value = slots.value.filter((_, i) => i !== index);
  persist();
}

/** Moves a bay to the front — the tank the garage is understood to open on. */
export function makePrimary(index: number): void {
  const slot = slots.value[index];
  if (!slot || index === 0) return;
  slots.value = [slot, ...slots.value.filter((_, i) => i !== index)];
  persist();
}

/**
 * This player's stored bays, or `null` if they have none.
 *
 * The distinction matters: `[]` is a garage somebody emptied on purpose and must
 * be given back as it was, while `null` is an account that has never parked
 * anything and is the only case `load` seeds. Storage that cannot be read at all
 * answers `null` too — a browser refusing `localStorage` should behave like a
 * fresh one, not like a cleared one.
 */
function read(playerKey: string): Favourite[] | null {
  try {
    const raw = window.localStorage.getItem(KEY_PREFIX + playerKey);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    // Validated field by field rather than trusted: this is hand-editable, and
    // a malformed entry must cost an empty garage rather than a crashed one.
    return parsed
      .filter(
        (e): e is Favourite =>
          !!e &&
          typeof e === 'object' &&
          typeof (e as Favourite).hullId === 'string' &&
          typeof (e as Favourite).turretId === 'string',
      )
      .slice(0, MAX_FAVOURITES);
  } catch {
    // Storage denied, or JSON that is not. Either way the garage opens.
    return null;
  }
}

function persist(): void {
  if (!loadedFor) return;
  try {
    window.localStorage.setItem(KEY_PREFIX + loadedFor, JSON.stringify(slots.value));
  } catch {
    // A preference that failed to save must not stop the player driving.
  }
}

/** Test seam: forgets what has been loaded so a fresh player can be read in. */
export function resetFavourites(): void {
  slots.value = [];
  loadedFor = null;
}
